import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { SelectionPosition } from "@/hooks/use-text-selection"

import {
  ArrowUpRight,
  BookOpen,
  Brain,
  ChevronRight,
  FileText,
  Globe,
  MessageSquare,
  Search,
  ShieldCheck,
} from "@/components/icons"
import { useTrustedSourcesSelection } from "@/features/trusted-sources/hooks/use-trusted-sources-selection"
import { useFeatureEnabled } from "@/hooks/use-feature-enabled"
import { useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { useSubmenuPositioning } from "@/hooks/use-submenu-positioning"

import { TrustedSourcesSubmenu } from "./trusted-sources-submenu"

type TextSelectionMenuProps = {
  selectedText: string
  position: SelectionPosition
  isOpen: boolean
  sessionType: string
  onClose: () => void
  onAskAbout: () => void
  onWebSearch: () => void
  onWebSearchTrusted: () => void
  onCreateDocument: () => void
  onStartResearch: () => void
  onFactCheck: () => void
  onBreakOutChat: () => void
  onAddToMemory: () => void
  /** Personal-answer selections must not offer memory or document export. */
  suppressExportActions?: boolean
}

export function TextSelectionMenu({
  selectedText,
  position,
  isOpen,
  sessionType,
  onClose,
  onAskAbout,
  onWebSearch,
  onWebSearchTrusted,
  onCreateDocument,
  onStartResearch,
  onFactCheck,
  onBreakOutChat,
  onAddToMemory,
  suppressExportActions = false,
}: TextSelectionMenuProps) {
  const { t } = useTranslation()
  const [showTrustedSourcesSubmenu, setShowTrustedSourcesSubmenu] = useState(false)

  // Check which features are enabled
  const hasResearchFeature = useFeatureEnabled("research")
  const canUseWebSearch = useWebSearchEnabled()
  const { sources } = useTrustedSourcesSelection(sessionType)
  const hasTrustedSources = sources.length > 0

  // Feature flag for Fact-check (in separate branch)
  const hasFactCheckFeature = false // TODO: Enable when Fact-check is merged

  const { menuRef, getSubmenuClasses } = useSubmenuPositioning({
    submenuWidth: 256,
    gap: 4,
    trigger: showTrustedSourcesSubmenu,
  })

  if (!isOpen || !selectedText) {
    return null
  }

  const truncatedText =
    selectedText.length > 50 ? `${selectedText.substring(0, 50)}...` : selectedText

  const trustedSubmenuClasses = getSubmenuClasses("z-[10000]")

  return (
    <div
      ref={menuRef}
      aria-label="Text selection actions menu"
      className="bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 fixed z-[9999] min-w-[8rem] overflow-visible rounded-md border p-1 shadow-md"
      data-text-selection-menu="true"
      role="menu"
      tabIndex={0}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose()
        }
      }}
    >
      <div className="w-64 overflow-visible">
        {/* Ask about */}
        <button
          className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
          role="menuitem"
          type="button"
          onClick={() => {
            onAskAbout()
            onClose()
          }}
        >
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span>{t("text_selection.ask_about", { defaultValue: "Ask about..." })}</span>
        </button>

        {/* Add to Memory — not offered for a personal answer */}
        {!suppressExportActions && (
          <button
            className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
            role="menuitem"
            type="button"
            onClick={() => {
              onAddToMemory()
              onClose()
            }}
          >
            <Brain className="h-4 w-4 shrink-0" />
            <span>{t("text_selection.add_to_memory", { defaultValue: "Add to memory" })}</span>
          </button>
        )}

        {canUseWebSearch && (
          <button
            className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
            role="menuitem"
            type="button"
            onClick={() => {
              onWebSearch()
              onClose()
            }}
          >
            <Globe className="h-4 w-4 shrink-0" />
            <span>{t("text_selection.web_search", { defaultValue: "Web search about..." })}</span>
          </button>
        )}

        {/* Web search (trusted sources) */}
        {canUseWebSearch && hasTrustedSources && (
          <div className="relative">
            <button
              className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
              onClick={() => setShowTrustedSourcesSubmenu(!showTrustedSourcesSubmenu)}
            >
              <ShieldCheck className="h-4 w-4 shrink-0" />
              <span>
                {t("text_selection.web_search_trusted", {
                  defaultValue: "Web search (trusted sources)",
                })}
              </span>
              <ChevronRight className="ml-auto h-4 w-4 shrink-0" />
            </button>
            {showTrustedSourcesSubmenu && (
              <div className={trustedSubmenuClasses}>
                <TrustedSourcesSubmenu
                  sessionType={sessionType}
                  onSelectAndClose={() => {
                    setShowTrustedSourcesSubmenu(false)
                    onWebSearchTrusted()
                    onClose()
                  }}
                />
              </div>
            )}
          </div>
        )}

        {hasResearchFeature && <div className="bg-border -mx-1 my-1 h-px" />}

        {!suppressExportActions && (
          <button
            className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
            onClick={() => {
              onCreateDocument()
              onClose()
            }}
          >
            <FileText className="h-4 w-4 shrink-0" />
            <span>
              {t("text_selection.create_document", {
                defaultValue: "Create document from selection",
              })}
            </span>
          </button>
        )}

        {/* Start Research */}
        {hasResearchFeature && (
          <button
            className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
            onClick={() => {
              onStartResearch()
              onClose()
            }}
          >
            <BookOpen className="h-4 w-4 shrink-0" />
            <span>
              {t("text_selection.start_research", { defaultValue: "Start Research on..." })}
            </span>
          </button>
        )}

        {/* Fact-check (placeholder) - Hidden until feature is merged */}
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
        {hasFactCheckFeature && (
          <button
            className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
            onClick={() => {
              onFactCheck()
              onClose()
            }}
          >
            <Search className="h-4 w-4 shrink-0" />
            <span>{t("text_selection.fact_check", { defaultValue: "Fact-check this..." })}</span>
          </button>
        )}

        <div className="bg-border -mx-1 my-1 h-px" />

        {/* Break out to new chat */}
        <button
          className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:shrink-0"
          onClick={() => {
            onBreakOutChat()
            onClose()
          }}
        >
          <ArrowUpRight className="h-4 w-4 shrink-0" />
          <span>
            {t("text_selection.break_out_chat", {
              defaultValue: "Break out section to new chat",
            })}
          </span>
        </button>

        {/* Preview of selected text */}
        <div className="bg-border -mx-1 my-1 h-px" />
        <div className="px-2 py-1.5">
          <p className="text-muted-foreground text-xs italic">"{truncatedText}"</p>
        </div>
      </div>
    </div>
  )
}
